/**
 * SELECT SEQUENCE (B8): one decision, shared by the card, the route strip and
 * the route meter, about *when* the numbers arrive.
 *
 *   selection → "drawing"  the meter counts with route:progress (B4, eased p)
 *             → "docked"   at route:start + durationMs − 120 (= Tarr − 120):
 *                          the card enters, its Facts take the final values,
 *                          the meter FLIPs into them and hides.
 *
 * When no draw is expected (reduced motion, the site, a future corridor, no
 * route) the sequence docks at once. When no route event arrives it docks after
 * max(600 ms, 0.4·Tc + 250 ms) — B4 may emit route:start at t = 0 or when the
 * line starts drawing (0.4·Tc) — and never later than Tarr + 1500 ms, so the
 * card can never wait forever.
 *
 * Announcements are not made here: B10's announcer owns the §H.3 select text.
 *
 * Singleton with a reference count: StrictMode double mounts and several
 * consumers share one set of bus/store subscriptions and timers.
 */
import mitt from "mitt";
import { displayTravel, type Place } from "@/contracts/data";
import { on } from "@/store/bus";
import { selectCategory, selectPlace } from "@/store/selectors";
import { appStore } from "@/store/store";
import { dockDelayMs, drawStartMs, tarrMs } from "./timing";

export type SeqPhase = "idle" | "drawing" | "docked";

export interface SeqSnapshot {
  readonly id: string | null;
  readonly phase: SeqPhase;
  /** Increments with every new selection (A → B included). */
  readonly seq: number;
  /** The selection came from the keyboard on a map pin: move focus to the card heading when it opens (§H.1). */
  readonly focusHeading: boolean;
  /** The dock followed a counted draw (the meter lands in the Facts). */
  readonly drew: boolean;
}

export interface SeqProgress { id: string; p: number }

type SeqEvents = {
  progress: SeqProgress;
  dock: { id: string; seq: number; drew: boolean };
};

/** No route event yet: dock after this long at the least (task rule: 600 ms). */
export const NO_EVENT_MS = 600;
/** Slack after the draw would have started, for B4 emitting route:start at 0.4·Tc. */
export const DRAW_SLACK_MS = 250;
/** Hard cap after the expected arrival. */
export const HARD_CAP_MS = 1500;

const events = mitt<SeqEvents>();
const listeners = new Set<() => void>();
const IDLE: SeqSnapshot = { id: null, phase: "idle", seq: 0, focusHeading: false, drew: false };
let snap: SeqSnapshot = IDLE;
let lastProgress: SeqProgress | null = null;
let lastStart: { placeId: string; at: number; durationMs: number } | null = null;
let refs = 0;
let offs: Array<() => void> = [];
let timers: Array<ReturnType<typeof setTimeout>> = [];

const now = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());

function publish(next: SeqSnapshot): void {
  snap = next;
  for (const l of [...listeners]) l();
}

function clearTimers(): void {
  for (const t of timers) clearTimeout(t);
  timers = [];
}

function later(ms: number, fn: () => void): void {
  timers.push(setTimeout(fn, Math.max(0, ms)));
}

const kindOf = (p: Place): string | null => selectCategory(appStore.getState(), p.category)?.kind ?? null;

/** B4 draws a hero route for this selection (so the numbers should wait for it). */
export function expectsDraw(p: Place, reduced: boolean): boolean {
  return !reduced && !p.isSite && !!p.route && kindOf(p) !== "future";
}

/** When the card docks if no route event arrives (ms after the selection). */
export function fallbackDockMs(D: number): number {
  return Math.max(NO_EVENT_MS, drawStartMs(D) + DRAW_SLACK_MS);
}

/** Reduced motion right now: the app flag or the OS setting (no React needed). */
function reducedNow(): boolean {
  if (appStore.getState().reducedMotion) return true;
  try {
    return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

function keyboardFocused(): boolean {
  if (typeof document === "undefined") return false;
  const a = document.activeElement;
  try {
    return !!a && a !== document.body && a.matches(":focus-visible");
  } catch {
    return false;
  }
}

function dock(seq: number): void {
  if (snap.seq !== seq || snap.phase !== "drawing" || !snap.id) return;
  clearTimers();
  const drew = lastProgress != null && lastProgress.id === snap.id;
  publish({ ...snap, phase: "docked", drew });
  events.emit("dock", { id: snap.id, seq, drew });
}

function scheduleFromStart(seq: number, startedAt: number, durationMs: number): void {
  clearTimers();
  const elapsed = now() - startedAt;
  later(dockDelayMs(durationMs) - elapsed, () => dock(seq));
  later(durationMs + HARD_CAP_MS - elapsed, () => dock(seq));
}

function begin(id: string | null, initial = false): void {
  clearTimers();
  lastProgress = null;
  const seq = snap.seq + 1;
  if (!id) {
    publish({ id: null, phase: "idle", seq, focusHeading: false, drew: false });
    return;
  }
  const s = appStore.getState();
  const p = selectPlace(s, id);
  const focusHeading = !initial && s.selectSource === "map" && keyboardFocused();
  if (!p || initial || !expectsDraw(p, reducedNow())) {
    publish({ id, phase: "docked", seq, focusHeading, drew: false });
    events.emit("dock", { id, seq, drew: false });
    return;
  }
  publish({ id, phase: "drawing", seq, focusHeading, drew: false });
  // B4 may have announced this draw synchronously, before our store subscriber ran.
  if (lastStart && lastStart.placeId === id && now() - lastStart.at < 250) {
    scheduleFromStart(seq, lastStart.at, lastStart.durationMs);
    return;
  }
  const D = displayTravel(p)?.km ?? p.crowKm;
  later(fallbackDockMs(D), () => dock(seq)); // no route event: don't keep the card waiting
  later(tarrMs(D) + HARD_CAP_MS, () => dock(seq)); // hard cap
}

function onRouteStart(e: { placeId: string; durationMs: number }): void {
  lastStart = { placeId: e.placeId, at: now(), durationMs: e.durationMs };
  if (snap.phase !== "drawing" || e.placeId !== snap.id) return;
  scheduleFromStart(snap.seq, lastStart.at, e.durationMs);
}

function onRouteProgress(e: { placeId: string; p: number }): void {
  if (e.placeId !== snap.id) return;
  lastProgress = { id: e.placeId, p: Math.min(1, Math.max(0, e.p)) };
  events.emit("progress", lastProgress);
  if (e.p >= 1 && snap.phase === "drawing") dock(snap.seq);
}

function onRouteEnd(e: { placeId: string }): void {
  if (e.placeId === snap.id && snap.phase === "drawing") dock(snap.seq);
}

/** Start the controller (idempotent, reference counted). Returns release. */
export function acquireSequence(): () => void {
  if (refs++ === 0) {
    offs = [
      appStore.subscribe((s, prev) => {
        if (s.selectedId !== prev.selectedId) begin(s.selectedId);
      }),
      on("route:start", onRouteStart),
      on("route:progress", onRouteProgress),
      on("route:drawn", onRouteEnd),
      on("route:cleared", onRouteEnd),
    ];
    // A selection that exists before anyone listened (deep link, remount) shows at once.
    const cur = appStore.getState().selectedId;
    if (cur !== snap.id || snap.phase === "drawing") begin(cur, true);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (--refs === 0) {
      for (const off of offs) off();
      offs = [];
      clearTimers();
    }
  };
}

export function subscribeSequence(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export const getSequence = (): SeqSnapshot => snap;

/** Latest route:progress for the current selection (null before the draw starts). */
export const getProgress = (): SeqProgress | null => lastProgress;

export function onSequence<K extends keyof SeqEvents>(type: K, fn: (e: SeqEvents[K]) => void): () => void {
  events.on(type, fn);
  return () => events.off(type, fn);
}

/* ── dock target: the card's Facts km figure, registered by the card ─── */

export interface DockTarget {
  fig: HTMLElement; //          the figure the meter lands on
  card: HTMLElement | null; //  the entering card (its transform is undone when measuring); null when embedded
}

let dockTarget: DockTarget | null = null;

export function setDockTarget(t: DockTarget | null): void {
  dockTarget = t;
}

export const getDockTarget = (): DockTarget | null => (dockTarget && dockTarget.fig.isConnected ? dockTarget : null);

/** Test hook: reset the singleton. */
export function __resetSequence(): void {
  clearTimers();
  for (const off of offs) off();
  offs = [];
  refs = 0;
  snap = IDLE;
  lastProgress = null;
  lastStart = null;
  dockTarget = null;
  listeners.clear();
  events.all.clear();
}
